import { useState, useRef, useEffect, forwardRef } from 'react';
import { ChevronDown, Plus } from 'lucide-react';

export interface ComboBoxOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface ComboBoxProps {
  label?: string;
  error?: string;
  hint?: string;
  options: ComboBoxOption[];
  value: string;
  onChange: (value: string) => void;
  onCreateNew?: (name: string) => Promise<void>;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  createLabel?: string; // Label for "Create new" action, e.g., "Create new manufacturer"
}

export const ComboBox = forwardRef<HTMLDivElement, ComboBoxProps>(
  (
    {
      label,
      error,
      hint,
      options,
      value,
      onChange,
      onCreateNew,
      placeholder = 'Select or type to create...',
      disabled = false,
      required = false,
      id,
      createLabel = 'Create new',
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [createError, setCreateError] = useState<string>('');
    const [activeIndex, setActiveIndex] = useState<number>(-1);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const comboBoxId = id || label?.toLowerCase().replace(/\s+/g, '-');
    const listboxId = `${comboBoxId}-listbox`;
    const createOptionId = `${comboBoxId}-create-option`;

    // Get the display label for the selected value
    const selectedOption = options.find((opt) => opt.value === value);
    const displayValue = selectedOption?.label || '';

    // Filter options based on search query
    const filteredOptions = options.filter((opt) =>
      opt.label.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Check if search query matches exactly with an existing option
    const exactMatch = options.some(
      (opt) => opt.label.toLowerCase() === searchQuery.toLowerCase()
    );

    // Show "Create new" option if we have onCreateNew, query is not empty, and no exact match
    const showCreateOption = onCreateNew && searchQuery.trim() && !exactMatch;

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
          setSearchQuery('');
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
      }
    }, [isOpen]);

    const handleInputClick = () => {
      if (!disabled) {
        setIsOpen(true);
      }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
      setIsOpen(true);
      setActiveIndex(-1);
      setCreateError('');
    };

    const handleSelectOption = (optionValue: string) => {
      onChange(optionValue);
      setIsOpen(false);
      setSearchQuery('');
    };

    const handleCreateNew = async () => {
      if (!onCreateNew || !searchQuery.trim()) return;

      setIsCreating(true);
      setCreateError('');
      try {
        await onCreateNew(searchQuery.trim());
        setSearchQuery('');
        setIsOpen(false);
        inputRef.current?.focus();
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to create new option';
        setCreateError(errorMessage);
        console.error('Failed to create new option:', error);
      } finally {
        setIsCreating(false);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      const totalOptions = filteredOptions.length + (showCreateOption ? 1 : 0);

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
          } else {
            setActiveIndex((prev) => (prev < totalOptions - 1 ? prev + 1 : prev));
          }
          break;

        case 'ArrowUp':
          e.preventDefault();
          if (isOpen) {
            setActiveIndex((prev) => (prev > 0 ? prev - 1 : -1));
          }
          break;

        case 'Home':
          if (isOpen) {
            e.preventDefault();
            setActiveIndex(0);
          }
          break;

        case 'End':
          if (isOpen) {
            e.preventDefault();
            setActiveIndex(totalOptions - 1);
          }
          break;

        case 'Enter':
          e.preventDefault();
          if (isOpen) {
            if (activeIndex >= 0 && activeIndex < filteredOptions.length) {
              handleSelectOption(filteredOptions[activeIndex].value);
            } else if (showCreateOption && activeIndex === filteredOptions.length) {
              handleCreateNew();
            } else if (showCreateOption && activeIndex === -1) {
              handleCreateNew();
            }
          }
          break;

        case 'Escape':
          setIsOpen(false);
          setSearchQuery('');
          setActiveIndex(-1);
          setCreateError('');
          break;

        case 'Tab':
          setIsOpen(false);
          setSearchQuery('');
          setActiveIndex(-1);
          break;
      }
    };

    return (
      <div ref={ref} className="w-full">
        {label && (
          <label
            htmlFor={comboBoxId}
            className="mb-1.5 block text-sm font-medium text-foreground"
          >
            {label}
            {required && <span className="ml-1 text-error">*</span>}
          </label>
        )}
        <div ref={containerRef} className="relative">
          <div className="relative">
            <input
              ref={inputRef}
              id={comboBoxId}
              type="text"
              role="combobox"
              aria-expanded={isOpen}
              aria-controls={listboxId}
              aria-activedescendant={
                activeIndex >= 0 && activeIndex < filteredOptions.length
                  ? `${comboBoxId}-option-${activeIndex}`
                  : activeIndex === filteredOptions.length && showCreateOption
                  ? createOptionId
                  : undefined
              }
              aria-autocomplete="list"
              aria-required={required}
              value={isOpen ? searchQuery : displayValue}
              onChange={handleInputChange}
              onClick={handleInputClick}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={disabled}
              required={required}
              className={`
                block w-full rounded-md border bg-background-subtle text-foreground
                focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary
                disabled:cursor-not-allowed disabled:opacity-50
                h-10 pl-3 pr-10 text-sm
                ${error || createError ? 'border-error' : 'border-border'}
              `}
            />
            <button
              type="button"
              onClick={handleInputClick}
              disabled={disabled}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-foreground-subtle"
            >
              <ChevronDown className="h-4 w-4" />
            </button>
          </div>

          {isOpen && (
            <div
              id={listboxId}
              role="listbox"
              className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-border bg-background-elevated shadow-lg"
            >
              {filteredOptions.length === 0 && !showCreateOption && (
                <div className="px-3 py-2 text-sm text-foreground-muted">
                  No options found
                </div>
              )}

              {filteredOptions.map((option, index) => (
                <button
                  key={option.value}
                  id={`${comboBoxId}-option-${index}`}
                  role="option"
                  type="button"
                  aria-selected={value === option.value}
                  onClick={() => handleSelectOption(option.value)}
                  disabled={option.disabled}
                  className={`
                    w-full px-3 py-2 text-left text-sm
                    hover:bg-background-subtle
                    disabled:cursor-not-allowed disabled:opacity-50
                    ${value === option.value ? 'bg-background-subtle font-medium' : ''}
                    ${activeIndex === index ? 'bg-background-subtle' : ''}
                  `}
                >
                  {option.label}
                </button>
              ))}

              {showCreateOption && (
                <button
                  id={createOptionId}
                  role="option"
                  type="button"
                  aria-selected={false}
                  onClick={handleCreateNew}
                  disabled={isCreating}
                  className={`
                    flex w-full items-center gap-2 border-t border-border px-3 py-2 text-left text-sm text-primary hover:bg-background-subtle disabled:cursor-not-allowed disabled:opacity-50
                    ${activeIndex === filteredOptions.length ? 'bg-background-subtle' : ''}
                  `}
                >
                  <Plus className="h-4 w-4" />
                  <span>
                    {isCreating ? 'Creating...' : `${createLabel}: "${searchQuery}"`}
                  </span>
                </button>
              )}
            </div>
          )}
        </div>

        {(error || createError) && (
          <p className="mt-1.5 text-sm text-error">{error || createError}</p>
        )}
        {hint && !error && !createError && (
          <p className="mt-1.5 text-sm text-foreground-muted">{hint}</p>
        )}
      </div>
    );
  }
);

ComboBox.displayName = 'ComboBox';
